/**
 * Firestore — draft documents (sensei-drafts collection, default DB).
 * Track C3.
 *
 * Collection: sensei-drafts
 * Database:   (default) — use getFirebaseFirestore()
 *
 * RULES: Never write undefined/null to Firestore.
 *        Always run removeUndefined() before writes.
 *
 * Field names copied character-for-character from reference:
 *   reactjs/src/lib/userProfile.ts lines 367-424
 */
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/platform/firebase/client";
import type { Draft } from "@/modes/draft/types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Recursively strips undefined and null values from any object/array.
 * Firestore rejects writes that contain undefined fields.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) {
    return obj
      .map(removeUndefined)
      .filter((item) => item !== null && item !== undefined);
  }
  if (typeof obj === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeUndefined(value);
      if (cleanedValue !== undefined && cleanedValue !== null) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }
  return obj;
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

/**
 * Create a new draft document and return the full Draft.
 * Mirrors React's createDraft() in userProfile.ts line 368.
 */
export async function createDraft(
  userId: string,
  title: string,
  query: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response?: any,
  mode?: "doc" | "deck"
): Promise<Draft> {
  const db = getFirebaseFirestore();

  const draft: Omit<Draft, "id"> = {
    userId,
    title,
    query,
    ...(response !== undefined && { response }),
    mode: mode || "doc",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Filter out undefined values to prevent Firestore errors
  const cleanDraft = Object.fromEntries(
    Object.entries(draft).filter(([, value]) => value !== undefined)
  );

  const docRef = await addDoc(collection(db, "sensei-drafts"), cleanDraft);
  return { id: docRef.id, ...draft };
}

/** Fetch a single draft by ID. Returns null if not found. */
export async function getDraft(draftId: string): Promise<Draft | null> {
  const db = getFirebaseFirestore();
  const snap = await getDoc(doc(db, "sensei-drafts", draftId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Draft;
}

/**
 * Fetch all drafts for a user, ordered by most-recently-updated.
 * Mirrors React's getUserDrafts() in userProfile.ts line 389.
 */
export async function getUserDrafts(userId: string): Promise<Draft[]> {
  const db = getFirebaseFirestore();
  const q = query(
    collection(db, "sensei-drafts"),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc"),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Draft);
}

/**
 * Update draft fields (merge). Runs removeUndefined before writing.
 * Mirrors React's updateDraft() in userProfile.ts line 410.
 */
export async function updateDraft(
  draftId: string,
  data: Partial<Draft>
): Promise<void> {
  const db = getFirebaseFirestore();
  const ref = doc(db, "sensei-drafts", draftId);

  const cleanData = removeUndefined({
    ...data,
    updatedAt: new Date().toISOString(),
  }) as Record<string, unknown>;

  await setDoc(ref, cleanData, { merge: true });
}

/** Hard-delete a draft. */
export async function deleteDraft(draftId: string): Promise<void> {
  const db = getFirebaseFirestore();
  await deleteDoc(doc(db, "sensei-drafts", draftId));
}

/**
 * Count how many drafts a user has in Firestore.
 * Used for free-plan gating (max 2 saved drafts).
 */
export async function countUserDrafts(userId: string): Promise<number> {
  const db = getFirebaseFirestore();
  const q = query(
    collection(db, "sensei-drafts"),
    where("userId", "==", userId)
  );
  const snap = await getDocs(q);
  return snap.size;
}
